import React, { useState, useCallback, useEffect, useRef } from 'react';
import { toCloudFrontUrl } from '../services/imageService';

type LoadState = 'loading' | 'loaded' | 'error';

interface ShimmerImageProps {
  /** Image source URL (S3 or CloudFront - auto-transformed) */
  src: string | undefined | null;
  /** Alt text for accessibility */
  alt: string;
  /** Width in pixels or CSS value */
  width?: number | string;
  /** Height in pixels or CSS value */
  height?: number | string;
  /** CSS aspect-ratio value (e.g., "1/1", "16/9") */
  aspectRatio?: string;
  /** Additional classes for the container */
  className?: string;
  /** Additional classes for the img element */
  imgClassName?: string;
  /** Object-fit behavior */
  objectFit?: 'cover' | 'contain' | 'fill' | 'none';
  /** Fallback icon to show on error */
  fallbackIcon?: React.ReactNode;
  /** Fallback image URL on error */
  fallbackSrc?: string;
  /** Background color for fallback state */
  fallbackBgColor?: string;
  /** Callback when image loads */
  onLoad?: () => void;
  /** Callback when image fails to load */
  onError?: () => void;
  /** Use eager loading for above-the-fold images */
  priority?: boolean;
  /** Disable shimmer effect */
  disableShimmer?: boolean;
  /** Shimmer variant for different backgrounds */
  variant?: 'light' | 'dark';
}

/**
 * ShimmerImage - Image component with shimmer loading effect
 *
 * Shows a shiny gradient animation while image loads, then smoothly
 * transitions to the actual image. Handles errors with fallback.
 *
 * @example
 * <ShimmerImage
 *   src={imageUrl}
 *   alt="Product"
 *   width={72}
 *   height={72}
 *   objectFit="contain"
 *   fallbackIcon={<Package size={24} className="text-gray-400" />}
 * />
 */
const ShimmerImage: React.FC<ShimmerImageProps> = ({
  src,
  alt,
  width,
  height,
  aspectRatio,
  className = '',
  imgClassName = '',
  objectFit = 'cover',
  fallbackIcon,
  fallbackSrc,
  fallbackBgColor = 'bg-gray-100',
  onLoad,
  onError,
  priority = false,
  disableShimmer = false,
  variant = 'light'
}) => {
  // If no src, start in error state
  const [loadState, setLoadState] = useState<LoadState>(src ? 'loading' : 'error');
  const imgRef = useRef<HTMLImageElement | null>(null);

  // Reset loadState when src changes (e.g. lazily resolved from a hook).
  useEffect(() => {
    setLoadState(src ? 'loading' : 'error');
  }, [src]);

  const handleLoad = useCallback(() => {
    setLoadState('loaded');
    onLoad?.();
  }, [onLoad]);

  const handleError = useCallback(() => {
    setLoadState('error');
    onError?.();
  }, [onError]);

  // Transform S3 URLs to CloudFront
  const transformedSrc = toCloudFrontUrl(src);

  // Catch the cached / already-complete case: with `loading="lazy"`, an image
  // can finish loading (or arrive from cache) before React attaches the onLoad
  // listener — the event then never fires and the shimmer hangs forever. After
  // mount and on every src change, peek at the underlying <img> and resolve
  // the state synchronously if the browser already has the bytes.
  useEffect(() => {
    if (!transformedSrc || loadState !== 'loading') return;
    const img = imgRef.current;
    if (!img) return;
    if (img.complete) {
      if (img.naturalWidth > 0) handleLoad();
      else handleError();
    }
  }, [transformedSrc, loadState, handleLoad, handleError]);

  // Safety-net timeout: if neither onLoad nor onError fired within 20s, drop
  // out of the shimmer state and show the fallback so the UI doesn't hang.
  useEffect(() => {
    if (loadState !== 'loading' || !transformedSrc) return;
    const t = window.setTimeout(() => {
      setLoadState((s) => (s === 'loading' ? 'error' : s));
    }, 20000);
    return () => window.clearTimeout(t);
  }, [transformedSrc, loadState]);

  // Container inline styles for dimensions
  const containerStyle: React.CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
    aspectRatio: aspectRatio,
  };

  // Shimmer gradient based on variant
  const shimmerGradient = variant === 'dark'
    ? 'bg-gradient-to-r from-white/5 via-white/15 to-white/5'
    : 'bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200';

  // Object-fit class
  const objectFitClass = {
    cover: 'object-cover',
    contain: 'object-contain',
    fill: 'object-fill',
    none: 'object-none'
  }[objectFit];

  return (
    <div
      className={`relative overflow-hidden ${className}`}
      style={containerStyle}
    >
      {/* Shimmer placeholder - visible during loading */}
      {loadState === 'loading' && !disableShimmer && (
        <div
          className={`absolute inset-0 ${shimmerGradient} bg-[length:200%_100%] animate-shimmer rounded-inherit`}
          aria-hidden="true"
        />
      )}

      {/* Actual image - hidden until loaded */}
      {transformedSrc && loadState !== 'error' && (
        <img
          ref={imgRef}
          src={transformedSrc}
          alt={alt}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          onLoad={handleLoad}
          onError={handleError}
          className={`
            w-full h-full
            ${objectFitClass}
            transition-opacity duration-200
            ${loadState === 'loaded' ? 'opacity-100' : 'opacity-0'}
            ${imgClassName}
          `}
        />
      )}

      {/* Error/fallback state */}
      {loadState === 'error' && (
        <div className={`absolute inset-0 ${fallbackBgColor} flex items-center justify-center`}>
          {fallbackSrc ? (
            <img
              src={fallbackSrc}
              alt={alt}
              className={`w-full h-full ${objectFitClass}`}
            />
          ) : fallbackIcon ? (
            fallbackIcon
          ) : null}
        </div>
      )}
    </div>
  );
};

export default React.memo(ShimmerImage);
